import React, { FC, useEffect } from "react";
import { connect } from "react-redux";
import { BulkActionProcessingStatus, type IBulkActionPayload } from "helpers/utils/bulk-manager";
import { CheckCircleOutline, ExpandMore } from "@material-ui/icons";
import { LinesheetsIcon } from "components/icons/linesheets";
import { CircularProgress } from "material-ui";
import { List, ListItem } from "material-ui/List";
import { IconButton, ListSubheader, Collapse, makeStyles } from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import SnackbarContent from "@material-ui/core/SnackbarContent";
import { format } from "lib/globalize-util";
import { theme } from "styles/theme";
import { IconAlertCircleRed } from "components/icons/assortments/icon-alert-circle-red";
import { IconAlertCircle } from "components/icons/assortments/icon-alert-circle";
import Close from "components/icons/close";
import { DATATEST } from "constants/integration";
import { closeBulkManager } from "actions/utilities/bulk-manager-actions";

interface IProps {
    processes: { [key: string]: IBulkActionPayload };
    open: boolean;
    onClose: () => void;
    scope?: any;
    type: string;
    view: "item" | "catalog";
}

const styles = {
    snackbar: { bottom: "4.5rem", boxShadow: "5px 5px 15px 5px rgba(0, 0, 0, 0.5)", borderRadius: "4px" },
    details: {
        backgroundColor: theme.palette.white,
        borderRadius: "0 0 4px 4px",
        padding: "0 0 0 0 !important",
        maxHeight: "300px",
        overflowY: "auto" as "auto",
    },
    group: {
        display: "inline-flex",
    },
};

interface IUseStylesProps {
    expanded: boolean;
}

const useStyles = makeStyles(() => ({
    root: {
        "& .MuiSnackbarContent-root": {
            color: theme.palette.white,
            flexWrap: "nowrap",
            fontFamily: "Lato, sans-serif !important",
            fontSize: "14px",
            width: "400px",
            boxShadow: "unset",
            borderRadius: (props: IUseStylesProps) => (props.expanded ? "4px 4px 0 0" : "4px"),
            transition: "border-radius 0.3s",
        },
        "& .MuiSnackbarContent-message": {
            display: "inline-flex",
            "& .MuiSvgIcon-root": {
                display: "flex",
                "&:first-child": {
                    fill: "currentColor",
                },
            },
            "& p": {
                display: "flex",
                fontSize: "14px",
                paddingTop: "2px",
                marginLeft: "10px",
            },
        },
        "& .MuiSnackbarContent-action ": {
            "& .MuiButtonBase-root": {
                marginLeft: "10px",
                height: "20px",
                width: "20px",
            },
        },
        "& .MuiListSubheader-sticky": {
            fontFamily: "Lato, sans-serif !important",
        },
    },
    expandIcon: {
        transition: "transform 0.3s",
        transform: (props: IUseStylesProps) => (props.expanded ? "rotate(180deg)" : "rotate(0deg)"),
    },
}));

const BulkManager: FC<IProps> = ({ processes, open, type, onClose, scope, view }) => {
    const [currentProcesses, setCurrentProcesses] = React.useState([]);
    const [expanded, setExpanded] = React.useState(false);
    const [successes, setSuccesses] = React.useState([]);
    const [errors, setErrors] = React.useState([]);
    const classes = useStyles({ expanded });
    const esSyncStatsVisible = React.useMemo(
        () =>
            view === "catalog" &&
            scope.accountType === "nuorder" &&
            (scope.brand.hasPreference("adaptive_dynamic_filters") ||
                scope.brand.hasPreference("improve_linesheets_search_performance")),
        [scope.accountType, scope.brand, view]
    );
    const viewStyle = React.useMemo(
        () => (!esSyncStatsVisible ? { right: "4.5rem" } : { right: "23.5rem" }),
        [esSyncStatsVisible]
    );

    useEffect(() => {
        setCurrentProcesses(Object.values(processes)?.filter((process) => process.brandId === scope?.brand?._id));
    }, [processes, scope]);

    useEffect(() => {
        setSuccesses(currentProcesses.filter((process) => process.status === BulkActionProcessingStatus.SUCCESS));
        setErrors(currentProcesses.filter((process) => process.status === BulkActionProcessingStatus.ERROR));
    }, [currentProcesses]);

    const handleClose = () => {
        onClose();
    };

    const handleExpand = () => {
        setExpanded(!expanded);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case BulkActionProcessingStatus.SUCCESS:
                return <CheckCircleOutline style={{ color: "#9fc4a9" }} />;
            case BulkActionProcessingStatus.ERROR:
                return <IconAlertCircleRed />;
            default:
                return <CircularProgress size={20} />;
        }
    };

    const overallStatus = React.useMemo(() => {
        let title = "";
        let icon = null;
        if (errors.length > 0) {
            title = format("Something went wrong.");
            icon = !successes.length ? <IconAlertCircleRed /> : <IconAlertCircle style={{ color: "#fae27f" }} />;
        } else if (successes.length === currentProcesses.length) {
            title = format("Products added!");
            icon = <CheckCircleOutline style={{ color: "#9fc4a9", fontSize: "2rem" }} />;
        } else {
            title = format(`Adding products to ${currentProcesses.length} ${type}...`);
            icon = <CircularProgress size={20} />;
        }
        return (
            <div data-test={DATATEST.BULK_MANAGER.HEADER} style={styles.group}>
                {icon}
                <p>{title}</p>
            </div>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors, successes, type]);

    return (
        <div className={classes.root}>
            <Snackbar
                open={open}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                style={{ ...styles.snackbar, right: viewStyle.right }}
            >
                <div data-test={DATATEST.BULK_MANAGER.TOAST_DRAWER}>
                    <SnackbarContent
                        message={overallStatus}
                        action={
                            <div>
                                <IconButton
                                    size="small"
                                    onClick={handleExpand}
                                    className={classes.expandIcon}
                                    data-test={DATATEST.BULK_MANAGER.EXPAND}
                                >
                                    <ExpandMore htmlColor={theme.palette.white} style={{ fontSize: "2.5rem" }} />
                                </IconButton>
                                {currentProcesses.length === successes.length + errors.length && (
                                    <IconButton
                                        size="small"
                                        onClick={handleClose}
                                        data-test={DATATEST.BULK_MANAGER.DISMISS}
                                    >
                                        <Close style={{ color: theme.palette.white }} />
                                    </IconButton>
                                )}
                            </div>
                        }
                    />
                    <Collapse
                        in={expanded}
                        timeout="auto"
                        unmountOnExit
                        style={{ maxWidth: "400px", overflowWrap: "break-word" }}
                    >
                        <List style={styles.details} data-test={DATATEST.BULK_MANAGER.PROCESSES}>
                            <ListSubheader
                                data-test={DATATEST.BULK_MANAGER.INFO}
                                style={{
                                    backgroundColor:
                                        errors.length + successes.length !== currentProcesses.length
                                            ? "#F0F2F2"
                                            : successes.length === currentProcesses.length
                                              ? "#EFF5F1"
                                              : errors.length === currentProcesses.length
                                                ? "#FBF0EF"
                                                : "#FEFBED",
                                    color: theme.palette.darkGrey,
                                    fontSize: "12px",
                                }}
                            >
                                {errors.length === 0 ? (
                                    <p>
                                        {format(`${successes.length} of ${currentProcesses.length} ${type} updated.`)}
                                    </p>
                                ) : (
                                    <p>
                                        {format(
                                            `${errors.length} of ${currentProcesses.length} ${type} didn't update. Refresh the page and try again.`
                                        )}
                                    </p>
                                )}
                            </ListSubheader>
                            {currentProcesses.map((process) => (
                                <ListItem
                                    key={process.id}
                                    leftIcon={<LinesheetsIcon style={{ color: theme.palette.darkGrey }} />}
                                    rightIcon={getStatusIcon(process.status)}
                                >
                                    {process.name}
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>
                </div>
            </Snackbar>
        </div>
    );
};

const mapStateToProps = (state, ownProps) => ({
    scope: state.scope,
    processes: state.bulkManager.processes,
    open: state.bulkManager.open,
    type: state.bulkManager.type,
    view: ownProps.view,
});

const mapDispatchToProps = (dispatch) => ({
    onClose: () => dispatch(closeBulkManager()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkManager);
